import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantBlock, ConvTurn, ToolName } from '../types.ts';
import { parseSSEChunk } from './sse-parser.ts';

// useChatStream owns the conversation state machine. Given a regulation id,
// it exposes:
//   - turns: ConvTurn[] (user + assistant) for rendering
//   - send(text): POSTs to /api/chat/stream, consumes SSE, builds blocks live
//   - cancel(): aborts the in-flight request
//   - status: 'idle' | 'streaming' | 'error'
//
// Event → block mapping:
//   - text → append to last text block (or create one)
//   - tool-start → push a tool block { status: 'running' }
//   - tool-end → update that tool block { status: 'done', result } and
//                also push a card block if the result shape maps to one
//   - done → mark stream complete
//   - error → mark error state, append a synthetic error text block

type Status = 'idle' | 'streaming' | 'error';

export function useChatStream(opts: { regulationId: string; initialSessionId?: string | null }) {
  const [turns, setTurns] = useState<ConvTurn[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(opts.initialSessionId ?? null);
  const abortRef = useRef<AbortController | null>(null);
  // Mirror `turns` into a ref so send() can read the committed value without
  // being re-created (and re-firing dependent effects) on every turn.
  const turnsRef = useRef<ConvTurn[]>(turns);
  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setStatus('idle');
  }, []);

  const send = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      cancel();

      const userTurn: ConvTurn = { role: 'user', text };
      const assistantTurn: ConvTurn = { role: 'assistant', blocks: [] };

      // Build the messages payload from the latest committed turns BEFORE
      // the optimistic update. React batches setters, so reading turns via a
      // second setter callback (the old approach) was racy: the assignment
      // happened too late and pendingTurns landed undefined. The ref always
      // holds the committed value so this is safe.
      const messages = toAnthropicMessages([...turnsRef.current, userTurn]);

      // Snapshot now, append both turns up-front so the UI shows the user
      // bubble + typing dots immediately.
      setTurns((prev) => [...prev, userTurn, assistantTurn]);
      setStatus('streaming');
      setErrorMessage(null);

      const controller = new AbortController();
      abortRef.current = controller;

      let response: Response;
      try {
        response = await fetch('/api/chat/stream', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages,
            regulation_id: opts.regulationId,
            session_id: sessionId,
          }),
          signal: controller.signal,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          handleError(setTurns, setStatus, setErrorMessage, (err as Error).message);
        }
        return;
      }

      if (!response.ok || !response.body) {
        handleError(
          setTurns,
          setStatus,
          setErrorMessage,
          `HTTP ${response.status} ${response.statusText}`,
        );
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { events, remainder } = parseSSEChunk(buffer);
          buffer = remainder;
          for (const event of events) {
            // Session announce: stash the id so subsequent sends bind to
            // the same persisted session. URL update happens in the page.
            if (event.type === 'session' && typeof event.session_id === 'string') {
              setSessionId(event.session_id);
            }
            applyEvent(setTurns, event);
          }
        }
        setStatus('idle');
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          handleError(setTurns, setStatus, setErrorMessage, (err as Error).message);
        }
      } finally {
        abortRef.current = null;
      }
    },
    [cancel, opts.regulationId, sessionId],
  );

  // Load a session's stored turns into the chat (when the user clicks a
  // history entry or navigates to /c/:id directly).
  const loadSession = useCallback(
    async (id: string) => {
      cancel();
      setSessionId(id);
      setStatus('idle');
      setErrorMessage(null);
      try {
        const res = await fetch(`/api/sessions/${id}`);
        if (!res.ok) {
          setErrorMessage(`failed to load session: HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        const reconstructed: ConvTurn[] = [];
        for (const t of data.turns ?? []) {
          if (t.role === 'user') {
            reconstructed.push({ role: 'user', text: t.content?.text ?? '' });
          } else if (t.role === 'assistant') {
            const blocks: AssistantBlock[] = [];
            for (const b of t.content?.blocks ?? []) {
              if (b.kind === 'text' && typeof b.text === 'string') {
                blocks.push({ kind: 'text', text: b.text });
              } else if (b.kind === 'tool') {
                blocks.push({
                  kind: 'tool',
                  tool: (b.tool as ToolName) ?? 'validate_team',
                  args: typeof b.args === 'string' ? b.args : '',
                  status: 'done',
                  elapsed: '✓',
                });
              } else if (b.kind === 'tool-result' && typeof b.tool === 'string') {
                // Reconstruct the card block the same way useChatStream does
                // live, via cardFromToolResult.
                const card = cardFromToolResult({ tool: b.tool, result: b.result });
                if (card) blocks.push(card);
              }
            }
            reconstructed.push({ role: 'assistant', blocks });
          }
        }
        setTurns(reconstructed);
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : String(err));
      }
    },
    [cancel],
  );

  const reset = useCallback(() => {
    cancel();
    setTurns([]);
    setSessionId(null);
    setStatus('idle');
    setErrorMessage(null);
  }, [cancel]);

  return { turns, send, cancel, loadSession, reset, status, errorMessage, sessionId };
}

function toAnthropicMessages(
  turns: readonly ConvTurn[],
): Array<{ role: 'user' | 'assistant'; content: string }> {
  // Minimal reconstruction: send only user text + assistant text blocks
  // (skip tool-use / tool-result internals since the backend reconstructs
  // those server-side). Future enhancement: persist & replay full content.
  const out: Array<{ role: 'user' | 'assistant'; content: string }> = [];
  for (const t of turns) {
    if (t.role === 'user') {
      out.push({ role: 'user', content: t.text });
    } else {
      const text = t.blocks
        .filter((b): b is Extract<AssistantBlock, { kind: 'text' }> => b.kind === 'text')
        .map((b) => b.text)
        .join('\n');
      if (text) out.push({ role: 'assistant', content: text });
    }
  }
  return out;
}

function handleError(
  setTurns: React.Dispatch<React.SetStateAction<ConvTurn[]>>,
  setStatus: React.Dispatch<React.SetStateAction<Status>>,
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>,
  message: string,
) {
  setStatus('error');
  setErrorMessage(message);
  setTurns((prev) => {
    const last = prev[prev.length - 1];
    if (!last || last.role !== 'assistant') return prev;
    const updated: ConvTurn = {
      role: 'assistant',
      blocks: [...last.blocks, { kind: 'text', text: `[error: ${message}]` }],
    };
    return [...prev.slice(0, -1), updated];
  });
}

function applyEvent(
  setTurns: React.Dispatch<React.SetStateAction<ConvTurn[]>>,
  event: Record<string, unknown>,
) {
  setTurns((prev) => {
    const last = prev[prev.length - 1];
    if (!last || last.role !== 'assistant') return prev;
    const blocks = [...last.blocks];

    switch (event.type) {
      case 'text': {
        if (typeof event.text !== 'string') return prev;
        const lastBlock = blocks[blocks.length - 1];
        if (lastBlock && lastBlock.kind === 'text') {
          blocks[blocks.length - 1] = { kind: 'text', text: lastBlock.text + event.text };
        } else {
          blocks.push({ kind: 'text', text: event.text });
        }
        break;
      }
      case 'tool-start': {
        blocks.push({
          kind: 'tool',
          tool: (event.tool as ToolName) ?? 'validate_team',
          args: typeof event.args === 'string' ? event.args : '',
          status: 'running',
          elapsed: '…',
        });
        break;
      }
      case 'tool-end': {
        // Find the matching tool-start block and mark done. Match by tool_use_id
        // when present, else by the most recent running tool of the same name.
        const useId = event.tool_use_id;
        for (let i = blocks.length - 1; i >= 0; i--) {
          const b = blocks[i];
          if (b.kind !== 'tool') continue;
          if (b.status !== 'running') continue;
          if (useId && (b as any)._useId && (b as any)._useId !== useId) continue;
          blocks[i] = { ...b, status: 'done', elapsed: '✓' };
          break;
        }
        // Also push a card block when the tool result has a known shape.
        const card = cardFromToolResult(event);
        if (card) {
          // Cross-card dedup for video-moments: two parallel
          // search_video_moments calls in the same assistant turn (e.g. one
          // per anchor species in a build flow) frequently return the same
          // creator video. Without this guard the user sees the same card
          // rendered twice. Drop any video already shown in an earlier
          // video-moments card in this turn; if the new card becomes empty,
          // skip it entirely.
          if (card.kind === 'card' && card.card === 'video-moments') {
            const seen = new Set<string>();
            for (const b of blocks) {
              if (b.kind !== 'card' || b.card !== 'video-moments') continue;
              const prior = ((b.data as { results?: any[] })?.results ?? []) as any[];
              for (const r of prior) {
                const id = videoIdFromUrl(r?.source_url ?? '');
                if (id) seen.add(id);
              }
            }
            const incoming = ((card.data as { results?: any[] })?.results ?? []) as any[];
            const filtered = incoming.filter((r) => {
              const id = videoIdFromUrl(r?.source_url ?? '');
              return id ? !seen.has(id) : true;
            });
            if (filtered.length === 0) break;
            card.data = { ...(card.data as object), results: filtered, count: filtered.length };
          }
          blocks.push(card);
        }
        break;
      }
      case 'done':
      case 'error':
        // No-op for turn-state; handled by status state. Errors get an
        // appended message via handleError above.
        break;
    }

    return [...prev.slice(0, -1), { role: 'assistant', blocks }];
  });
}

// Map known tool results to typed card blocks for rendering.
function cardFromToolResult(event: Record<string, unknown>): AssistantBlock | null {
  const tool = event.tool as string;
  const result = event.result as any;
  if (!result || result.ok === false) return null;

  switch (tool) {
    case 'validate_team':
      // Render the team card only when validation passed AND the handler
      // echoed the parsed roster. Failed validations don't get a card; the
      // agent will fix and retry.
      if (result.valid === true && Array.isArray(result.team)) {
        return { kind: 'card', card: 'built-team', data: result };
      }
      return null;
    case 'predict_opponent_leads':
      return { kind: 'card', card: 'lead-prediction', data: result };
    case 'calc_damage':
      return { kind: 'card', card: 'damage-grid', data: result };
    case 'calc_damage_matrix':
      return { kind: 'card', card: 'damage-matrix', data: result };
    case 'find_min_ev_to_survive':
      return { kind: 'card', card: 'ev-spread', data: result };
    case 'get_speed_tier_table':
      return { kind: 'card', card: 'speed-tier', data: result };
    case 'get_tournament_precedents':
      return { kind: 'card', card: 'tournament-precedents', data: result };
    case 'get_build_suggestions':
      return { kind: 'card', card: 'build-suggestions', data: result };
    case 'semantic_search_teams':
      return { kind: 'card', card: 'team', data: result };
    case 'search_extracted_claims':
      return { kind: 'card', card: 'claim', data: result };
    case 'search_video_moments':
      // No card if the search came back empty — let the agent's prose
      // explain "nothing matched" rather than a noisy empty card.
      if (!Array.isArray(result?.results) || result.results.length === 0) return null;
      return { kind: 'card', card: 'video-moments', data: result };
    case 'get_replica_codes':
      return { kind: 'card', card: 'replica-code', data: result };
    case 'parse_opponent_species':
      return { kind: 'card', card: 'species-parser', data: result };
    case 'emit_verdict':
      return { kind: 'card', card: 'verdict', data: result };
    default:
      return null;
  }
}

// Pull the 11-char YouTube video id out of a chunk's `source_url`. Mirrors
// the parser inside VideoMomentsCard — kept inline (rather than imported)
// because this hook should not pull React-component code.
function videoIdFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const qp = url.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  if (qp) return qp[1];
  const bare = url.match(/(^|[/=])([A-Za-z0-9_-]{11})(?:$|[&?#])/);
  return bare ? bare[2] : null;
}
